import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QrCode } from 'react-qrcode-pretty';

import { pushGtmEvent } from 'analytics/gtm';
import { useMediaQuery } from 'react-responsive';

import ullyLauncher from 'assets/images/ully-launcher.png';

import { CloseIcon, ExternalLinkIcon, WhatsAppIcon } from './icons';
import {
  BlockTitle,
  Body,
  CloseButton,
  DesktopBlock,
  HeadingArea,
  HeadingText,
  HeadingTitle,
  LauncherAvatar,
  LauncherButton,
  LauncherDismiss,
  LauncherWrapper,
  Loader,
  OutlinedButton,
  Overlay,
  Panel,
  PanelContent,
  PhoneLink,
  QrBox,
  QrCard,
  QrText,
  QrTexts,
  QrTitle,
  RedirectContent,
  RedirectFallback,
  RedirectText,
  RedirectTexts,
  RedirectTitle,
  VisuallyHidden,
} from './styles';
import {
  ULLY_MOBILE_BREAKPOINT,
  ULLY_REDIRECT_DELAY_MS,
  ULLY_WHATSAPP_APP_URL,
  ULLY_WHATSAPP_PHONE_LABEL,
  ULLY_WHATSAPP_WEB_URL,
} from './constants';
import { ULLY_OPEN_EVENT } from './ullyBus';

type UllyState = 'closed' | 'choice' | 'redirecting';

const pushEvent = (eventLabel: string): void => {
  pushGtmEvent({
    event: 'click_cta',
    eventCategory: 'ully',
    eventAction: 'click',
    eventLabel,
    elementType: 'chat',
  });
};

const Ully = (): JSX.Element => {
  const isMobile = useMediaQuery({ maxWidth: ULLY_MOBILE_BREAKPOINT });

  const [state, setState] = useState<UllyState>('closed');
  const [dismissed, setDismissed] = useState(false);

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const redirectTimer = useRef<number | null>(null);

  const clearRedirect = useCallback((): void => {
    if (redirectTimer.current !== null) {
      window.clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
  }, []);

  const close = useCallback((): void => {
    clearRedirect();
    setState('closed');
  }, [clearRedirect]);

  const redirectOnMobile = useCallback((): void => {
    setState('redirecting');
    pushEvent('Ully - redirecionamento WhatsApp app');

    clearRedirect();
    redirectTimer.current = window.setTimeout(() => {
      window.location.href = ULLY_WHATSAPP_APP_URL;
    }, ULLY_REDIRECT_DELAY_MS);
  }, [clearRedirect]);

  const open = useCallback((): void => {
    setDismissed(false);

    if (isMobile) {
      redirectOnMobile();
      return;
    }

    pushEvent('Ully - abrir modal');
    setState('choice');
  }, [isMobile, redirectOnMobile]);

  const openWhatsAppWeb = useCallback((): void => {
    pushEvent('Ully - Abrir WhatsApp Web');
    window.open(ULLY_WHATSAPP_WEB_URL, '_blank', 'noopener,noreferrer');
    close();
  }, [close]);

  useEffect(() => {
    const handleOpen = (): void => open();
    window.addEventListener(ULLY_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(ULLY_OPEN_EVENT, handleOpen);
  }, [open]);

  useEffect(() => {
    if (state === 'closed') return undefined;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [state, close]);

  useEffect(() => {
    if (state !== 'closed') closeButtonRef.current?.focus();
  }, [state]);

  useEffect(() => {
    if (state === 'closed') return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [state]);

  useEffect(() => clearRedirect, [clearRedirect]);

  const content = (
    <>
      <LauncherWrapper hidden={dismissed}>
        <LauncherButton
          type="button"
          id="hi-custom-widget"
          onClick={open}
          aria-haspopup="dialog"
          aria-expanded={state !== 'closed'}
        >
          <LauncherAvatar src={ullyLauncher} alt="Ully — posso ajudar?" />
        </LauncherButton>
        <LauncherDismiss
          type="button"
          id="close-bot"
          aria-label="Ocultar a Ully"
          onClick={() => setDismissed(true)}
        >
          X
        </LauncherDismiss>
      </LauncherWrapper>

      {state !== 'closed' && (
        <Overlay role="presentation" onClick={close}>
          <Panel
            role="dialog"
            aria-modal="true"
            aria-label="Fale com a Ully pelo WhatsApp"
            onClick={(event) => event.stopPropagation()}
          >
            <CloseButton
              ref={closeButtonRef}
              type="button"
              aria-label="Fechar"
              onClick={close}
            >
              <CloseIcon />
            </CloseButton>

            {state === 'choice' ? (
              <PanelContent>
                <HeadingArea>
                  <HeadingTitle>Fale com a Ully pelo WhatsApp</HeadingTitle>
                  <HeadingText>
                    A nossa assistente virtual agora atende diretamente pelo
                    WhatsApp. Escolha como deseja iniciar a conversa.
                  </HeadingText>
                </HeadingArea>

                <Body>
                  <DesktopBlock>
                    <BlockTitle>Continuar pelo computador</BlockTitle>
                    <OutlinedButton type="button" onClick={openWhatsAppWeb}>
                      <WhatsAppIcon />
                      Abrir WhatsApp Web
                      <ExternalLinkIcon />
                    </OutlinedButton>
                  </DesktopBlock>

                  <QrCard>
                    <QrTexts>
                      <QrTitle>Continuar pelo celular</QrTitle>
                      <QrText>
                        Escaneie o QR Code com a câmera do celular para abrir a
                        conversa com nossa assistente virtual no WhatsApp.
                      </QrText>
                      <PhoneLink
                        href={ULLY_WHATSAPP_APP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => pushEvent('Ully - telefone WhatsApp')}
                      >
                        {`(${ULLY_WHATSAPP_PHONE_LABEL})`}
                      </PhoneLink>
                    </QrTexts>
                    <QrBox>
                      <QrCode
                        value={ULLY_WHATSAPP_APP_URL}
                        size={112}
                        level="M"
                        margin={0}
                        padding={0}
                        color="#000000"
                        bgColor="#FFFFFF"
                        variant="standard"
                        canvasProps={{
                          'aria-label':
                            'QR Code para conversar com a Ully no WhatsApp',
                        }}
                      />
                    </QrBox>
                  </QrCard>
                </Body>
              </PanelContent>
            ) : (
              <PanelContent>
                <RedirectContent>
                  <Loader role="status" aria-live="polite">
                    <VisuallyHidden>Redirecionando</VisuallyHidden>
                  </Loader>

                  <RedirectTexts>
                    <RedirectTitle>Você está sendo redirecionado</RedirectTitle>
                    <RedirectText>
                      A Ully, nossa assistente virtual, agora atende diretamente
                      pelo WhatsApp. Em instantes, você será direcionado para a
                      conversa. Se o navegador solicitar uma confirmação, basta
                      aceitar.
                    </RedirectText>
                    <RedirectFallback>
                      <span>Não foi direcionado automaticamente? </span>
                      <a
                        href={ULLY_WHATSAPP_APP_URL}
                        onClick={() => pushEvent('Ully - fallback manual')}
                      >
                        Clique aqui
                      </a>
                      <span>{` (número ${ULLY_WHATSAPP_PHONE_LABEL})`}</span>
                    </RedirectFallback>
                  </RedirectTexts>
                </RedirectContent>
              </PanelContent>
            )}
          </Panel>
        </Overlay>
      )}
    </>
  );

  return createPortal(content, document.body);
};

export default Ully;
